import { Card, Box, Icon, Tooltip, Divider } from "@mui/material";
import StatsCounterCard from "components/Cards/StatsCounterCard";
import PropTypes from "prop-types";

import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import HealthStatusCard from "components/Cards/HealthStatusCard";
import DeviceStatsCard from "components/Cards/DeviceStatsCard";
import { useSoftUIController } from "hooks/context";
import { getConnectivityIcon, getDeviceTypeIcon } from "utils/device";
import moment from "moment";
import { Link } from "react-router-dom";
import MapLinkButton from "components/Buttons/MapLinkButton";
import { useEffect, useState } from "react";
import { getNamingConventions } from "utils/formatting";

function DeviceOverviewTab({ user, device }) {

    const [controller] = useSoftUIController();
    const { deviceLayout, userPermissions } = controller;

    const showAdvancedDeviceLevel = userPermissions.indexOf("devices-advanced:view") >= 0;
    const accessUserLocation = userPermissions.indexOf('misc:user-location') >= 0;
    const isClearways = userPermissions.indexOf('clearways') >= 0;

    const [header, subheader, triheader] = getNamingConventions(device.name);
    const showStats = (typeof device?.temperature != 'undefined' && typeof device?.battery != 'undefined' && typeof device?.rssi != 'undefined' && typeof device?.humidity != 'undefined') ? true : false;

    // var rel_time = device?.last_heartbeat ? Math.floor((moment().unix() - device?.last_heartbeat) / 60) : null;
    // const device_liveview_last_update = typeof rel_time != null ? (rel_time < 1 ? 'Less than a minute ago' : (rel_time == 1 ? `1 minute ago` : `${rel_time} minutes ago`)) : device.last_updated;

    var rel_time = null;
    if (device?.expected_online_at) {
        if (!isNaN(device?.expected_online_at - moment().unix())) {
            rel_time = Math.floor((device?.expected_online_at - moment().unix()) / 60);
        }
    }

    // console.log(rel_time)
    var device_liveview_next_update = device.last_updated;

    if (typeof rel_time != null) {
        if (rel_time < 1) {
            device_liveview_next_update = 'In less than a minute';
        }
        else if (rel_time == 1) {
            device_liveview_next_update = `In 1 minute`;
        }
        else if (rel_time < 60) {
            device_liveview_next_update = `In ${rel_time} minutes`;
        }
        else {
            device_liveview_next_update = `In over ${Math.floor(rel_time / 60) == 1 ? 'an hour' : `${Math.floor(rel_time / 60)} hours`}`;
        }
    }

    const [currentLocation, setCurrentLocation] = useState([0, 0]);

    useEffect(() => {
        if (accessUserLocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    // console.log(position.coords)
                    setCurrentLocation([latitude, longitude]);
                },
                (error) => {
                    console.error("Error detecting location:", error);
                    // Check if the error was specifically a timeout

                    if (error.code === error.TIMEOUT) {
                        console.warn("Location request timed out. Setting to 0,0.");
                    }
                    setCurrentLocation([0, 0]);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 15000, // 15 seconds in milliseconds
                    maximumAge: 0   // Force fresh location
                }
            );
        }
    }, [user]);


    const overviewInfo = (
        <Card sx={{ height: "100%", overflowY: 'auto', background: '#f9f9f9', borderRadius: '12px' }} >
            <SoftBox py={2} px={2}>
                <SoftBox display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                    {isClearways ? (
                        <>
                            <SoftBox mt={1} mx={1} display="flex" flexDirection="row" flexWrap="wrap" alignItems="baseline" sx={{ gap: 0.5 }}>
                                <SoftTypography variant={deviceLayout == "mobile" ? "h3" : "h2"} fontWeight="bold" sx={{ color: "#333", mr: 1, flexShrink: 1, width: 'fit-content' }}>{`eMinor ${header}`}</SoftTypography>
                                <SoftTypography variant="subtitle1" fontWeight="bold" textTransform="capitalize" sx={{ color: "#777", minWidth: "fit-content" }}>{typeof device?.stop?.site_id == 'undefined' ? `Site ID Not Set` : `(${device?.stop?.site_id})`}</SoftTypography>
                            </SoftBox>
                            <SoftBox pl={2} pr={1}>
                                {accessUserLocation && (
                                    <SoftBox sx={{
                                        verticalAlign: 'center',
                                        alignItems: 'center',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        backgroundColor: '#ddd',
                                        borderRadius: '50px',
                                        mb: 0.5
                                    }}>
                                        <MapLinkButton locations={{ user_loc: currentLocation, dest_loc: device.position }} backgroundColor={'#ddd'} dimensions={"30px"} />
                                    </SoftBox>
                                )}
                                <Tooltip title="View on Overview Map" placement="left">
                                    <Icon sx={{ cursor: 'pointer', fontSize: '28px !important' }} component={Link} to={"../../overview"} state={{ device }}>public</Icon>
                                </Tooltip>
                            </SoftBox>
                        </>
                    ) : (
                        <>
                            <SoftBox mt={1} mx={1} display="flex" flexDirection="row" flexWrap="wrap" alignItems="baseline" sx={{ gap: 0.5 }}>
                                <SoftTypography variant={deviceLayout == "mobile" ? "h3" : "h2"} fontWeight="bold" sx={{ color: "#333", mr: 1, flexShrink: 1, width: 'fit-content' }}>{header}</SoftTypography>
                                {subheader && <SoftTypography variant="subtitle1" fontWeight="bold" sx={{ color: "#777", minWidth: "fit-content" }}>{subheader}{triheader && ', '}</SoftTypography>}
                                {triheader && <SoftTypography variant="subtitle1" fontWeight="bold" sx={{ color: "#777", verticalAlign: 'bottom', lineHeight: 1.5 }}>{triheader}</SoftTypography>}
                            </SoftBox>
                            {device.active && (
                                <SoftBox pl={2} pr={1}>
                                    <Tooltip title="View on Overview Map" placement="left">
                                        <Icon sx={{ cursor: 'pointer', fontSize: '32px !important' }} component={Link} to={"../../overview"} state={{ device }}>public</Icon>
                                    </Tooltip>
                                </SoftBox>
                            )}
                        </>
                    )}
                </SoftBox>
                <SoftBox my={2}>
                    <Divider />
                </SoftBox>
                <Card>
                    <SoftBox mx={2} my={1}>
                        <HealthStatusCard statistics={device} showAdvancedDeviceLevel={showAdvancedDeviceLevel} transparent={true} />
                        {showStats && (
                            <>
                                <SoftBox my={1}>
                                    <Divider />
                                </SoftBox>
                                <Box display="flex" flexDirection="column" gap={1.5}>
                                    <Box display="flex" alignItems="center">
                                        <Box flex={1}>
                                            <StatsCounterCard
                                                count={device.battery}
                                                suffix="%"
                                                title="Battery"
                                                transparent={true}
                                            />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box flex={1}>
                                            <StatsCounterCard
                                                count={device.temperature}
                                                suffix={<>&deg;C</>}
                                                title="Temperature"
                                                transparent={true}
                                            />
                                        </Box>
                                    </Box>
                                    {showAdvancedDeviceLevel && (
                                        <>
                                            <Divider />
                                            <Box display="flex" alignItems="center">
                                                <Box flex={1}>
                                                    <StatsCounterCard
                                                        count={device.rssi}
                                                        suffix="-dB"
                                                        title="Signal Strength"
                                                        transparent={true}
                                                    />
                                                </Box>
                                                <Divider orientation="vertical" flexItem />
                                                <Box flex={1}>
                                                    <StatsCounterCard
                                                        count={device.humidity}
                                                        suffix="%"
                                                        title="Humidity"
                                                        transparent={true}
                                                    />
                                                </Box>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </>
                        )}
                        <SoftBox my={1}>
                            <Divider />
                        </SoftBox>
                        <DeviceStatsCard
                            icon={getConnectivityIcon(device, showAdvancedDeviceLevel)} transparent={true} variant={deviceLayout == "mobile" ? "h6" : "h4"}
                            title={{ text: getConnectivityIcon(device, showAdvancedDeviceLevel).text, subtext: device.organisation_id == "clearways" && device.connected_state != "offline" ? `Next Update: ${device_liveview_next_update}` : "", fontWeight: "medium" }}
                        />
                        <SoftBox my={1}>
                            <Divider />
                        </SoftBox>
                        <DeviceStatsCard
                            icon={getDeviceTypeIcon(device, showAdvancedDeviceLevel)} transparent={true} variant={deviceLayout == "mobile" ? "h6" : "h4"}
                            title={{ text: getDeviceTypeIcon(device, showAdvancedDeviceLevel).text, subtext: device.connected_state == "eco" ? `Next Update: ${device_liveview_next_update}` : "", fontWeight: "medium" }}
                        />
                        {showAdvancedDeviceLevel && (
                            <>
                                <SoftBox my={1}>
                                    <Divider />
                                </SoftBox>
                                <DeviceStatsCard icon={{ component: "fingerprint", colour: "dark" }} transparent={true} variant={deviceLayout == "mobile" ? "h6" : "h4"} title={{ text: device?.serial_number || "N/A", fontWeight: "medium" }} />
                            </>
                        )}
                    </SoftBox>
                </Card>
            </SoftBox >
        </Card >
    );

    return overviewInfo;
}

DeviceOverviewTab.propTypes = {
    user: PropTypes.object,
    device: PropTypes.object
}

export default DeviceOverviewTab;